import { engine } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import Scroll from 'src/game/components/shared/Scroll';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import {
  LeagueLeaderboard,
  LeagueLeaderboardEntry,
} from 'src/replicant/match2/leagues/leagues.ruleset';
import {
  getCurrentLeagueId,
  getLeagueReward,
} from 'src/replicant/match2/leagues/leagues.getters';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { animDuration } from 'src/lib/utils';
import PopupMatch2BaseFullscreen from 'src/game/minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import { LeaguesLeadeboardView } from 'src/game/minigame/match2-timestep/leagues/components/LeaguesLeadeboardView';
import { LeaguesPrize } from 'src/game/minigame/match2-timestep/leagues/components/LeaguesPrize';
import { downscale } from 'src/conf/scale';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';

const entryScale = 1.25;

export default class PopupChampionsLeagueClaim extends PopupMatch2BaseFullscreen {
  private readonly scroll: Scroll<
    LeagueLeaderboardEntry & { hideReward?: boolean },
    LeaguesLeadeboardView
  >;
  private readonly header: ImageScaleView;
  private readonly mainPlayer: LeaguesLeadeboardView;
  private readonly scrollArea: ImageScaleView;
  private tickHandler: () => void = () => void 0;
  private playerRank?: number;
  private rankText: LangBitmapFontTextView;
  private noRewards: LangBitmapFontTextView;
  private rewards: LeaguesPrize;
  private closing = false;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => {
        if (this.closing) return;
        this.closing = true;
        opts.close();
      },
      tracking: {
        feature: Feature.MATCH2.LEAGUES._,
        $subFeature: Feature.MATCH2.LEAGUES.LEADERBOARD,
      },
      bgColor: '#fae5b7',
      cta: {
        localeText: () => i18n('match2.tournament.claim').toLocaleUpperCase(),
        footerHeight: 170,
        onClick: async () => {
          if (this.closing) return;
          this.closing = true;
          opts.close();
        },
      },
    });

    const { width, height } = this.getView().style;
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';

    this.header = new ImageScaleView({
      superview: this.panel,
      image: 'assets/ui/championsLeague/popups/header.png',
      width: 720,
      height: 365,
      scaleMethod: 'cover',
    });

    const ribbon = new ImageScaleView({
      superview: this.header,
      image: `assets/ui/match2/leagues/big_ribbon.png`,
      width: 242,
      height: 66,
      centerOnOrigin: true,
      x: this.header.style.width * 0.5,
      y: 180,
    });

    new LangBitmapFontTextView({
      superview: ribbon,
      x: ribbon.style.width * 0.5,
      y: ribbon.style.height * 0.5 - 5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: ribbon.style.width,
      height: ribbon.style.height,
      color: '#ffffff',
      localeText: () => i18n('minigames.result').toLocaleUpperCase(),
    });

    new LangBitmapFontTextView({
      superview: this.header,
      x: this.header.style.width / 4,
      y: 250,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      width: 120,
      height: 36,
      size: 29,
      font: 'Title',
      color: '#f6c856',
      localeText: () => i18n('match2.tournament.rank'),
    });

    const rankFrame = new ImageScaleView({
      superview: this.header,
      image: 'assets/ui/match2/leagues/results_frame.png',
      width: 229,
      height: 60,
      x: this.header.style.width / 4,
      y: 310,
      centerOnOrigin: true,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 30, right: 30 },
        vertical: { top: 30, middle: 30, bottom: 30 },
      },
    });

    this.rankText = new LangBitmapFontTextView({
      superview: rankFrame,
      x: rankFrame.style.width * 0.5 + 14,
      y: rankFrame.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      width: rankFrame.style.width - 14,
      height: rankFrame.style.height,
      size: 38,
      font: 'Title',
      color: '#ffffff',
      localeText: () => `# 9,999`,
    });

    new ImageScaleView({
      superview: rankFrame,
      image: 'assets/ui/match2/leagues/medal_leaderboard.png',
      width: 100,
      height: 100,
      y: rankFrame.style.height * 0.5,
      centerOnOrigin: true,
    });

    new LangBitmapFontTextView({
      superview: this.header,
      x: (3 * this.header.style.width) / 4,
      y: 250,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      width: 120,
      height: 36,
      size: 29,
      font: 'Title',
      color: '#f6c856',
      localeText: () => i18n('match2.tournament.reward'),
    });

    this.noRewards = new LangBitmapFontTextView({
      superview: this.header,
      x: (3 * this.header.style.width) / 4,
      y: 310,
      width: 229,
      height: 60,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 38,
      font: 'Title',
      color: '#ffffff',
      localeText: () => `-`,
    });

    this.rewards = new LeaguesPrize({
      superview: this.header,
      x: (3 * this.header.style.width) / 4,
      y: 310,
      scale: 1,
    });

    new View({
      superview: this.header,
      backgroundColor: '#ffffff',
      width: 720,
      height: 4,
      y: 366,
    });

    new ImageScaleView({
      superview: this.header,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: `assets/ui/championsLeague/popups/championsleague_${locale}.png`,
      width: downscale(848),
      height: downscale(133),
      centerOnOrigin: true,
      x: this.header.style.width * 0.5,
      y: 80,
    });

    const scrollHeight =
      height - this.footerHeight - this.header.style.height + 5;

    this.scrollArea = new ImageScaleView({
      superview: this.panel,
      width,
      height: scrollHeight,
      y: this.header.style.height,
      backgroundColor: '#FF0000',
      visible: false,
    });

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: this.scrollArea.style.x + 22,
        y: this.scrollArea.style.y + 5,
        width: this.scrollArea.style.width - 10,
        height: this.scrollArea.style.height - 10,
      },
      margin: 30,
      firstItemOffset: -15,
      lastItemOffset: -15,
    });

    this.mainPlayer = new LeaguesLeadeboardView({
      superview: this.panel,
      width: (width + 6) / entryScale,
      height: 82,
      scale: entryScale,
      x: this.scrollArea.style.x - 3,
      y: this.scrollArea.style.y + this.scrollArea.style.height,
    });
  }

  public init({ data }: { data: LeagueLeaderboard }) {
    super.init({});
    this.scroll.setItems(data.leaderboard);
    this.setupMainPlayer(data);
    this.rankText.localeText = () => `# ${data.playerRank}`;

    const rewards = getLeagueReward(data.playerRank);

    if (!rewards) {
      this.noRewards.show();
      this.rewards.hide();
      return;
    }

    this.noRewards.hide();
    this.rewards.show();
    this.rewards.setup(rewards[0]);
  }

  private createItem(
    superview: View,
    i: number,
    data: LeagueLeaderboardEntry & { hideReward?: boolean },
  ) {
    const entry = new LeaguesLeadeboardView({
      superview,
      scale: entryScale,
      width: 545,
      height: 75,
    });
    entry.setup(data);
    return entry;
  }

  private setItemData(
    i: number,
    data: LeagueLeaderboardEntry & { hideReward?: boolean },
    view: LeaguesLeadeboardView,
  ) {
    view.setup(data);
  }

  public override async fadeIn(): Promise<void> {
    this.tickHandler = this.tick.bind(this);
    engine.addListener('Tick', this.tickHandler);
    return super.fadeIn();
  }

  public override async fadeOut(): Promise<void> {
    engine.removeListener('Tick', this.tickHandler);
    await this.rewards.grantReward(this.root, { scale: 1.5 });
    return super.fadeOut();
  }

  private tick() {
    // Not properly initialized yet, probably the async getter isn't ready
    if (this.playerRank == null) {
      this.mainPlayer.hide();
      return;
    }

    if (this.scroll.isItemVisible(this.playerRank)) {
      this.mainPlayer.hide();
      return;
    }

    const top =
      this.scroll.getElementPosition(this.playerRank) <
      this.scroll.viewportUpperBounds;

    this.mainPlayer.updateOpts({
      visible: true,
      y: top
        ? this.scrollArea.style.y + 4
        : this.scrollArea.style.y +
          this.scrollArea.style.height -
          this.mainPlayer.style.height * entryScale -
          12,
    });
  }

  private setupMainPlayer(data: LeagueLeaderboard) {
    const { name, photo } = getPlayerProfile(
      user(),
      user().publicProfile?.name,
      user().publicProfile?.photo,
      user().profile?.name,
      user().profile?.photo,
    );

    const score = user().match2.leagues[getCurrentLeagueId(user())]?.score;
    this.mainPlayer.setup({
      id: user().id,
      rank: data.playerRank,
      score,
      name: name ?? '',
      photo: photo ?? '',
      me: true,
      hideReward: true,
      modifiedAt: 0,
    });
    this.playerRank = data!.playerRank;
    this.mainPlayer.onInputSelect = () =>
      this.scroll.centerOn(this.playerRank!, animDuration);
  }
}
